import { RefObject, useCallback, useEffect, useState } from 'react';

export type Placement = 'top' | 'bottom';

export interface CalloutPosition {
  top: number;
  left: number;
  placement: Placement;
  arrowLeft: number; // px, relative to the callout box
}

const GAP = 10; // space between trigger and callout (room for the arrow)
const EDGE = 8; // min distance from viewport edge
const ARROW_HALF = 8;

interface Options {
  anchorRef: RefObject<HTMLElement>;
  calloutRef: RefObject<HTMLElement>;
  open: boolean;
  preferred?: Placement;
}

export const useCalloutPosition = ({
  anchorRef,
  calloutRef,
  open,
  preferred = 'bottom',
}: Options): CalloutPosition | null => {
  const [position, setPosition] = useState<CalloutPosition | null>(null);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    const callout = calloutRef.current;

    if (!anchor) {
      console.warn(
        '[Callout] anchorRef is not attached to a DOM node. ' +
          'Did you forget ref={...} on the trigger, or is the trigger a ' +
          'custom component that needs forwardRef?',
      );
    }

    if (!anchor || !callout) return;

    const a = anchor.getBoundingClientRect();
    const { width, height } = callout.getBoundingClientRect();

    const spaceBelow = window.innerHeight - a.bottom;
    const spaceAbove = a.top;
    const needed = height + GAP + EDGE;

    // Flip only when the preferred side doesn't fit and the other side does.
    let placement: Placement = preferred;
    if (preferred === 'bottom' && spaceBelow < needed && spaceAbove >= needed) {
      placement = 'top';
    } else if (
      preferred === 'top' &&
      spaceAbove < needed &&
      spaceBelow >= needed
    ) {
      placement = 'bottom';
    }

    const top = placement === 'bottom' ? a.bottom + GAP : a.top - height - GAP;

    // Centre on the trigger, then clamp inside the viewport.
    const anchorCentre = a.left + a.width / 2;
    const left = Math.min(
      Math.max(anchorCentre - width / 2, EDGE),
      Math.max(window.innerWidth - width - EDGE, EDGE),
    );

    // Keep the arrow on the trigger even after clamping.
    const arrowLeft = Math.min(
      Math.max(anchorCentre - left, ARROW_HALF + 4),
      width - ARROW_HALF - 4,
    );

    setPosition((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.placement === placement &&
      prev.arrowLeft === arrowLeft
        ? prev
        : { top, left, placement, arrowLeft },
    );
  }, [anchorRef, calloutRef, preferred]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    update();

    const onChange = () => update();
    window.addEventListener('scroll', onChange, true); // capture: catches scrollable parents
    window.addEventListener('resize', onChange);

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onChange)
        : null;
    if (observer && calloutRef.current) observer.observe(calloutRef.current);

    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      observer?.disconnect();
    };
  }, [open, update, calloutRef]);

  return position;
};
